import { create } from 'zustand'
import type {
  SelectedTankArray,
  SchoolUpdateValuesArray,
  SchoolUpdateTableValuesArray,
} from '@/types/view'
import type { TankRelationMap } from '../group/useGroupStore'
import { SchoolState } from '../school/useSchoolStore'
import { useTankStore } from '../tank/useTankStore'

type HomeUpdateState = {
  updateDate: number | null
  selectedTanks: SelectedTankArray
  selectedTankIds: string[]
  schoolValues: SchoolUpdateValuesArray
  updateTableData: SchoolUpdateTableValuesArray
  refreshSchools: string[]
  dateBorderColor: string
  selectTanksBorderColor: string
  updateData: SchoolUpdateTableValuesArray
  beEmptyTanks: string[]
  tableBorderColor: string
  typeError: boolean
  requiredError: boolean
  requiredCheck: boolean
}

type HomeUpdateAction = {
  setUpdateDate: (updateDate: number | null) => void
  setSelectedTanks: (selectedTanks: SelectedTankArray, schoolIdList: TankRelationMap) => void
  setSchoolValues: (schoolVaues: SchoolUpdateValuesArray, storedSchoolList: SchoolState) => void
  setDateBorderColor: () => void
  setSelectedTanksBorderColor: () => void
  reset: () => void
  setInsertUpdateTableData: (updateTableData: SchoolUpdateTableValuesArray) => void
  checkRequired: () => void
}

const initialState: HomeUpdateState = {
  updateDate: null,
  selectedTanks: [],
  selectedTankIds: [],
  schoolValues: [],
  updateTableData: [],
  refreshSchools: [],
  dateBorderColor:
    '[&>div]:border [&>div]:border-tdpldarkgrey [&>div]:border-opacity-60 [&>div]:hover:border-tdpldarkgrey [&>div]:hover:border-opacity-60',
  selectTanksBorderColor: 'border border-tdpldarkgrey',
  updateData: [],
  beEmptyTanks: [],
  tableBorderColor: 'border border-tdpldarkgrey',
  typeError: false,
  requiredError: false,
  requiredCheck: false,
}

const useHomeUpdateStore = create<HomeUpdateState & HomeUpdateAction>((set) => ({
  ...initialState,
  setUpdateDate: (updateDate) => {
    set(() => ({
      updateDate: updateDate,
      dateBorderColor: updateDate
        ? '[&>div]:border [&>div]:border-tdpldarkgrey [&>div]:border-opacity-60 [&>div]:hover:border-tdpldarkgrey [&>div]:hover:border-opacity-60'
        : '[&>div]:border-2 [&>div]:border-tdplmaroon [&>div]:hover:border-tdplmaroon',
    }))
  },
  setSelectedTanks: (selectedTanks, schoolIdList) => {
    // Get tanks from store for ungrouped tanks fallback
    const tanks = useTankStore.getState().tanks

    set(() => ({
      selectedTanks: selectedTanks,
      selectedTankIds: selectedTanks.map((item) => item.tankId),
      refreshSchools: selectedTanks.map((item) => {
        // First try to get schoolId from schoolIdList (grouped tanks)
        const relation = schoolIdList[item.tankId]
        if (relation && relation.length > 0) {
          return relation[0].schoolId
        }
        // Fallback: get schoolId from tank store (ungrouped tanks)
        const tank = tanks[item.tankId]
        return tank?.schoolId || ''
      }),
      selectTanksBorderColor: 'border border-tdpldarkgrey',
      schoolValues: [],
      updateTableData: [],
      updateData: [],
    }))
  },
  setSchoolValues: (schoolValues, storedSchoolList) => {
    set((state) => ({
      updateTableData: schoolValues.map((item, index) => {
        // item.schoolId는 실제로 tankId (API 변환에서 임시 저장)
        // refreshSchools[index]가 실제 schoolId
        const actualSchoolId = state.refreshSchools[index]

        return {
          ...item,
          schoolId: actualSchoolId,  // 실제 schoolId로 교체
          tankName: state.selectedTanks[index].tankName,
          tankId: state.selectedTanks[index].tankId,
          resultingCount: Number(storedSchoolList[actualSchoolId]?.count || 0),
        }
      }),
    }))
  },
  setInsertUpdateTableData: (updateTableData) => {
    set(() => {
      const typeError = !updateTableData.every(
        (item) =>
          (Number.isInteger(item.harvest) || item.harvest === null) &&
          (Number.isInteger(item.mortality) || item.mortality === null) &&
          (Number.isInteger(item.feedTimes) || item.feedTimes === null),
      )
      return {
        updateTableData: updateTableData,
        // Keep tankId for v2 API (/tank/daily-records) which requires tank_id
        updateData: updateTableData.map((item) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { tankName: _, resultingCount: __, ...values } = item
          return values
        }),
        beEmptyTanks: updateTableData
          .filter(
            (item) =>
              (Number(item.resultingCount) || 0) - (Number(item.harvest) || 0) - (Number(item.mortality) || 0) <= 0 &&
              item.tankName,
          )
          .map((item) => {
            return item.tankName ? item.tankName : ''
          }),
        typeError: typeError,
        tableBorderColor: typeError ? 'border-2 border-tdplmaroon' : 'border border-tdpldarkgrey',
        requiredError: !updateTableData.every(
          (item) => (item.feedingAmount !== null) === (item.feedTimes !== null),
        ),
        requiredCheck: false,
      }
    })
  },
  setDateBorderColor: () => {
    set((state) => ({
      dateBorderColor: state.updateDate
        ? '[&>div]:border [&>div]:border-tdpldarkgrey [&>div]:border-opacity-60 [&>div]:hover:border-tdpldarkgrey [&>div]:hover:border-opacity-60'
        : '[&>div]:border-2 [&>div]:border-tdplmaroon [&>div]:hover:border-tdplmaroon [&>div]:border-opacity-60',
    }))
  },
  setSelectedTanksBorderColor: () => {
    set((state) => ({
      selectTanksBorderColor:
        state.selectedTankIds.length !== 0
          ? 'border border-tdpldarkgrey'
          : 'border-2 border-tdplmaroon',
    }))
  },
  reset: () => {
    set(initialState)
  },
  checkRequired: () => {
    set((state) => {
      return {
        tableBorderColor: state.requiredError
          ? 'border-2 border-tdplmaroon'
          : state.tableBorderColor,
        requiredCheck: state.requiredError,
      }
    })
  },
}))

export default useHomeUpdateStore
